import { useEffect, useState, useRef, useMemo } from 'react'
import { useMemoizedFn } from 'ahooks'
import { message as $message } from 'antd'
import { grantAuthV2UsingPOST, resendSmsAuthCodeUsingPOST } from '@/services/user'
import { wrapResponseData } from '@/utils/wrapRequest'
const getSensitiveOperationErrorCode = code => {
    return code?.split?.('-')?.[1]
}

export default ({ dataSource, length = 4, onOk }) => {
    const [countdown, setCountdown] = useState(0)
    const [sendCount, setSendCount] = useState(0)
    const [codes, setCodes] = useState(() => Array(length).fill(''))
    const timerRef = useRef(null)
    const inputRef = useRef(null)
    const [isFocused, setIsFocused] = useState(false)

    const sensitiveOperationErrorCode = getSensitiveOperationErrorCode(dataSource.code)

    const init = useMemoizedFn(() => {
        focusInput()
        startCountdown()
    })

    const focusInput = useMemoizedFn(() => {
        inputRef.current && inputRef.current.focus()
    })

    const sendCode = useMemoizedFn(async () => {
        await wrapResponseData(
            () =>
                resendSmsAuthCodeUsingPOST({
                    sensitiveOperationErrorCode
                }),
            {
                rejection: true
            }
        )

        startCountdown()
    })

    const startCountdown = useMemoizedFn(() => {
        setCountdown(60)
        setSendCount(prev => prev + 1)

        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                const next = prev - 1

                if (next <= 0) {
                    clearInterval(timerRef.current)
                }

                return next
            })
        }, 1000)
    })

    const keyupHandler = useMemoizedFn(e => {
        console.info('keyupHandler:', e)

        // 使用 event.code 判断数字键
        if (e.code.startsWith('Digit')) {
            // 主键盘数字键 (Digit0-Digit9)
            const digit = e.code.slice(5) // 移除 'Digit' 前缀
            if (/^[0-9]$/.test(digit)) {
                setCodes(prev => {
                    const index = prev.findIndex(item => item === '')
                    if (index >= 0) {
                        const newCode = [...prev]
                        newCode[index] = digit
                        return newCode
                    }
                    return prev
                })
            }
            return
        }

        if (e.code.startsWith('Numpad')) {
            // 小键盘数字键 (Numpad0-Numpad9)
            const digit = e.code.slice(6) // 移除 'Numpad' 前缀
            if (/^[0-9]$/.test(digit)) {
                setCodes(prev => {
                    const index = prev.findIndex(item => item === '')
                    if (index >= 0) {
                        const newCode = [...prev]
                        newCode[index] = digit
                        return newCode
                    }
                    return prev
                })
            }
            return
        }

        // Backspace 键处理
        if (e.code === 'Backspace') {
            setCodes(prev => {
                const index = prev.findLastIndex(item => item !== '')
                if (index >= 0) {
                    const newCode = [...prev]
                    newCode[index] = ''
                    return newCode
                }
                return prev
            })
        }
    })

    useEffect(() => {
        return () => {
            timerRef.current && clearTimeout(timerRef.current)
        }
    }, [])

    const checkCodesValid = useMemoizedFn(codes => {
        return codes.length === length && codes.every(item => /^[0-9]$/.test(item))
    })

    const submit = useMemoizedFn(async () => {
        const hasValidCode = checkCodesValid(codes)

        if (!hasValidCode) {
            const msg = `请输入${length}位数字验证码`
            $message.error(msg)
            focusInput()
            return Promise.reject(msg)
        }

        const { success, message, data } = await grantAuthV2UsingPOST({
            code: codes.join(''),
            sensitiveOperationErrorCode
        })

        if (!success || !data) {
            const msg = message || '验证失败'
            $message.warn(msg)
            focusInput()
            return Promise.reject(msg)
        }

        return onOk()
    })

    const activeCodeIndex = useMemo(() => {
        if (!isFocused) {
            return -1
        }

        return codes.findIndex(item => !item)
    }, [codes, isFocused])

    const isCodesValid = useMemo(() => {
        return codes.filter(Boolean).length >= length
    }, [codes, length])

    return {
        init,
        dataSource,
        sendCode,
        countdown,
        sendCount,
        activeCodeIndex,
        isCodesValid,
        codes,
        submit,
        inputRef,
        isFocused,
        setIsFocused,
        keyupHandler
    }
}
